import type { AdminConfigItemDto, JsonValue } from '@carcheck/shared';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigHistoryRow } from './ConfigHistoryRow';
import { ConfigKeyForm } from './ConfigKeyForm';
import { ConfigRowActions } from './ConfigRowActions';
import { ConfigTypeBadge } from './ConfigTypeBadge';
import { ConfigValueCell } from './ConfigValueCell';
import { describeConfigKey } from '../_lib/configDisplay';
import { configFormKind } from '../_lib/configForms';
import { shortActor } from '../_lib/formatActor';
import { useConfigRowEditor } from '../_lib/useConfigRowEditor';
import type { SetConfigOutcome } from '../_lib/useSetConfig';

/**
 * One row of the runtime-config table (AUT-131) — a thin orchestrator over
 * the editing state machine (`useConfigRowEditor`) + three presentational
 * cells (`ConfigValueCell` / `ConfigTypeBadge` / `ConfigRowActions`) + the
 * lazy history row (`ConfigHistoryRow`). Renders the data row, the form row
 * while a `deals.stages` / `specialists.fx` form is open (AUT-542,
 * `ConfigKeyForm`), and a sibling collapse row hosting the history.
 *
 * Architect guardrails (enforced in the extracted units):
 * 🟠 #1 type-specific editor + json parse-on-save (`useConfigRowEditor`);
 * 🟠 #2 `present:false` → read-only "не задано", no edit (cells);
 * 🟠 #3 PROACTIVE step-up via the page's `onSave` + the "нужен TOTP" pill;
 * 🟠 #4 a 400 → inline `error` pinned under the editor;
 * 🟠 #6 history is lazy — `ConfigHistory` fetches only once `historyOpen`.
 */
export interface ConfigRowProps {
  item: AdminConfigItemDto;
  /** PROACTIVE step-up write from the page; resolves the outcome. */
  onSave: (variables: { key: string; value: JsonValue }) => Promise<SetConfigOutcome>;
  /** True while ANY row's save is in flight (disables editors). */
  busy: boolean;
}

export const ConfigRow = ({ item, onSave, busy }: ConfigRowProps): ReactElement => {
  const { t } = useTranslation();
  const editor = useConfigRowEditor(item, onSave);
  const [historyOpen, setHistoryOpen] = useState(false);
  // AUT-542 — `deals.stages` / `specialists.fx` edit in a form row; the
  // JSON editor stays reachable from it («Править как JSON»).
  const formKind = configFormKind(item);
  const [formOpen, setFormOpen] = useState(false);

  const { label, humanised } = describeConfigKey(item.key, t);
  const keyHint = t('config.keyTooltip', { key: item.key });

  return (
    <>
      <TableRow hover data-testid={`config-row-${item.key}`}>
        <TableCell>
          {/*
            AUT-284 — the human description IS the row's identity; the dotted
            key is not shown. Igor: «нам вообще ключи не показывать, а надо
            просто отображать сами человеческие вещи».

            The key stays reachable without occupying the row: hovering the
            label reveals it, and expanding the row prints it in full (see
            the history cell below) so an operator on a call with a
            developer can still name the thing they are looking at. A key
            with no gloss registered falls back to the key itself, mono — a
            blank row would be worse than a technical one.
          */}
          <Tooltip title={keyHint}>
            <Typography
              component="div"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                width: 'fit-content',
                ...(humanised ? {} : { fontFamily: 'monospace' }),
              }}
            >
              {label}
            </Typography>
          </Tooltip>
        </TableCell>

        <TableCell data-testid="config-value-cell">
          <ConfigValueCell item={item} editor={editor} busy={busy} />
        </TableCell>

        <TableCell>
          <ConfigTypeBadge type={item.type} />
        </TableCell>

        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {item.lastChangedAt
              ? `${new Date(item.lastChangedAt).toLocaleDateString('ru-RU')} · ${
                  shortActor(item.lastChangedBy) ?? t('config.noValue')
                }`
              : t('config.noValue')}
          </Typography>
        </TableCell>

        <TableCell align="right">
          <ConfigRowActions
            editing={editor.editing}
            editable={item.present && !formOpen}
            busy={busy}
            historyOpen={historyOpen}
            onToggleHistory={() => setHistoryOpen((v) => !v)}
            onBeginEdit={formKind ? () => setFormOpen(true) : editor.begin}
            onSave={() => void editor.save()}
            onCancel={editor.cancel}
          />
        </TableCell>
      </TableRow>

      {formOpen && formKind ? (
        <TableRow>
          <TableCell colSpan={5}>
            <ConfigKeyForm
              item={item}
              kind={formKind}
              onSave={onSave}
              busy={busy}
              onClose={() => setFormOpen(false)}
              onEditJson={() => {
                setFormOpen(false);
                editor.begin();
              }}
            />
          </TableCell>
        </TableRow>
      ) : null}

      <ConfigHistoryRow configKey={item.key} keyHint={keyHint} open={historyOpen} />
    </>
  );
};
